import { useState } from "react";
import { Outlet, NavLink } from "react-router-dom";
import { LayoutDashboard, Package, Split, Truck, Users, FileText, LogOut, Settings, FileCheck, UserCheck, Menu, X, SendHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";

const Layout = ({ user, onLogout }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const menuItems = [
    { path: "/", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/inward", icon: Package, label: "Inward" },
    { path: "/segregation", icon: Split, label: "Segregation" },
    { path: "/dispatch", icon: Truck, label: "Dispatch" },
    { path: "/challan", icon: FileCheck, label: "Challan" },
    { path: "/staff", icon: Users, label: "Staff" },
    { path: "/reports", icon: FileText, label: "Reports" },
  ];

  // Add Requests page for staff and managers
  if (user?.role === 'staff' || user?.role === 'manager') {
    menuItems.push({ path: "/requests", icon: SendHorizontal, label: "Requests" });
  }

  if (user?.role === 'admin') {
    menuItems.push({ path: "/approvals", icon: UserCheck, label: "Approvals" });
    menuItems.push({ path: "/admin", icon: Settings, label: "Settings" });
  }

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="flex flex-col h-screen">
      {/* Header */}
      <header className="bg-[#5B7FC7] shadow-lg z-50">
        <div className="px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden text-white hover:bg-white/20 p-1"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="mobile-menu-toggle"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
            <img 
              src="https://customer-assets.emergentagent.com/job_hub-dispatch-mgmt/artifacts/08gxp2q2_rr%20logo.jpg" 
              alt="Recompose Logo" 
              className="w-8 h-8 sm:w-12 sm:h-12 object-contain bg-white rounded-lg p-0.5 sm:p-1"
            />
            <div className="hidden sm:block">
              <h1 className="text-lg sm:text-2xl font-bold tracking-wide text-white">Recompose</h1>
              <p className="text-[10px] sm:text-xs text-white/90">Recycling Management System</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-6">
            <div className="text-right hidden sm:block">
              <p className="font-semibold text-white text-sm sm:text-base">{user?.name}</p>
              <p className="text-[10px] sm:text-xs capitalize text-white/80">{user?.role}</p>
            </div>
            <Button
              onClick={onLogout}
              variant="ghost"
              className="text-white hover:bg-white/20 px-2 sm:px-3"
              size="sm"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline ml-2">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden relative">
        {/* Mobile overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={closeSidebar}
          />
        )}

        {/* Sidebar */}
        <aside className={`
          fixed lg:static inset-y-0 left-0 z-40
          w-56 sm:w-64 bg-white/95 backdrop-blur-sm border-r border-gray-200 shadow-lg
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          top-[52px] sm:top-[68px] lg:top-0
        `}>
          <nav className="p-2 sm:p-4 space-y-1 sm:space-y-2">
            {menuItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={closeSidebar}
                data-testid={`nav-${item.label.toLowerCase()}`}
                style={{ color: '#009245' }}
                className={({ isActive }) =>
                  `flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-medium transition-all duration-300 text-sm sm:text-base ${
                    isActive
                      ? "bg-gradient-to-r from-[#1B5E20] to-[#6BB6A] text-white shadow-md"
                      : "hover:bg-gradient-to-r hover:from-gray-100 hover:to-green-50"
                  }`
                }
              >
                <item.icon className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                <span className="font-semibold truncate">{item.label}</span>
              </NavLink>
            ))}
          </nav>
        </aside>
        
        {/* Main Content */}
        <main className="flex-1 overflow-auto bg-gradient-to-br from-gray-50 via-white to-green-50/30">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
